import React, {
  useEffect,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { Input } from "@ndd/ui";
import {
  localParts,
  searchClients,
  shortDateLabel,
  type ScheduleApi,
} from "../../../lib/rd-schedule-actions";
import type { ActionClient } from "./action-types";
import { Field } from "./action-parts";

// NO-876. The Client search in New appointment: type two letters, pick from
// the list with the arrow keys and Enter, or the mouse. The search runs after
// a short pause so each keystroke is not a request.

const MIN_LETTERS = 2;
const PAUSE_MS = 250;

export type SearchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "done"; results: ActionClient[] };

export function useClientSearch(
  api: ScheduleApi,
  rdId: string | null,
  text: string,
  enabled: boolean
): SearchState {
  const [state, setState] = useState<SearchState>({ status: "idle" });
  const query = text.trim();

  useEffect(() => {
    if (!enabled || query.length < MIN_LETTERS) {
      setState({ status: "idle" });

      return undefined;
    }

    let current = true;
    setState({ status: "loading" });

    const timer = setTimeout(() => {
      searchClients(api, rdId, query)
        .then((results) => {
          if (current) setState({ status: "done", results });
        })
        .catch(() => {
          if (current) setState({ status: "error" });
        });
    }, PAUSE_MS);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [api, rdId, query, enabled]);

  return state;
}

function searchNote(state: SearchState, query: string): string | null {
  if (query.trim().length < MIN_LETTERS)
    return "Type at least two letters of the Client's name.";

  if (state.status === "loading") return "Searching…";

  if (state.status === "error") return "The search did not work. Type again to retry.";

  if (state.status === "done" && state.results.length === 0)
    return "No Clients match that name.";

  return null;
}

function lastVisit(client: ActionClient, timeZone: string): string | null {
  return client.lastVisitAt
    ? `Last visit ${shortDateLabel(localParts(client.lastVisitAt, timeZone).date)}`
    : null;
}

interface ClientPicker {
  text: string;
  active: number;
  listOpen: boolean;
  state: SearchState;
  results: ActionClient[];
  choose: (client: ActionClient) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onType: (text: string) => void;
  onFocus: () => void;
  onBlur: () => void;
}

function useClientPicker(props: {
  api: ScheduleApi;
  rdId: string | null;
  value: ActionClient | null;
  onChange: (client: ActionClient | null) => void;
}): ClientPicker {
  const { api, rdId, value, onChange } = props;
  const [text, setText] = useState(value?.displayName ?? "");
  const [active, setActive] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const state = useClientSearch(api, rdId, text, listOpen && value === null);
  const results = state.status === "done" ? state.results : [];

  const choose = (client: ActionClient) => {
    setText(client.displayName);
    setListOpen(false);
    onChange(client);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const move = KEY_MOVES[event.key];

    if (move !== undefined) {
      event.preventDefault();
      setActive((index) => Math.min(Math.max(index + move, 0), results.length - 1));
    } else if (event.key === "Enter" && listOpen && results[active]) {
      event.preventDefault();
      choose(results[active]);
    } else if (event.key === "Escape" && listOpen) {
      event.stopPropagation();
      setListOpen(false);
    }
  };

  const onType = (next: string) => {
    setText(next);
    setActive(0);
    setListOpen(true);

    if (value) onChange(null);
  };

  return {
    text,
    active,
    listOpen,
    state,
    results,
    choose,
    onKeyDown,
    onType,
    onFocus: () => setListOpen(value === null),
    onBlur: () => setListOpen(false),
  };
}

const KEY_MOVES: Record<string, number | undefined> = { ArrowDown: 1, ArrowUp: -1 };

function clientHint(
  value: ActionClient | null,
  picker: ClientPicker,
  timeZone: string
): string | undefined {
  if (value) return lastVisit(value, timeZone) ?? undefined;

  if (!picker.listOpen) return undefined;

  return searchNote(picker.state, picker.text) ?? undefined;
}

export function ClientSearch(props: {
  api: ScheduleApi;
  rdId: string | null;
  timeZone: string;
  value: ActionClient | null;
  disabled: boolean;
  onChange: (client: ActionClient | null) => void;
  /** Mirrors whether the results list is open, so Escape can close it first. */
  listOpenRef?: { current: boolean };
}): ReactElement {
  const picker = useClientPicker(props);

  if (props.listOpenRef)
    props.listOpenRef.current = picker.listOpen && picker.results.length > 0;

  const showList = picker.listOpen && picker.results.length > 0;

  return (
    <Field
      id="new-client"
      label="Client"
      hint={clientHint(props.value, picker, props.timeZone)}
    >
      <Input
        id="new-client"
        role="combobox"
        aria-expanded={showList}
        aria-controls="new-client-results"
        aria-activedescendant={showList ? optionId(picker.active) : undefined}
        aria-autocomplete="list"
        aria-describedby="new-client-hint"
        autoComplete="off"
        value={picker.text}
        disabled={props.disabled}
        onKeyDown={picker.onKeyDown}
        onFocus={picker.onFocus}
        onBlur={picker.onBlur}
        onChange={(event) => picker.onType(event.target.value)}
      />
      {showList ? (
        <ClientResults
          results={picker.results}
          active={picker.active}
          onChoose={picker.choose}
        />
      ) : null}
    </Field>
  );
}

function optionId(index: number): string {
  return `new-client-option-${index}`;
}

function ClientResults(props: {
  results: ActionClient[];
  active: number;
  onChoose: (client: ActionClient) => void;
}): ReactElement {
  return (
    <ul
      id="new-client-results"
      role="listbox"
      aria-label="Matching Clients"
      onMouseDown={(event) => event.preventDefault()}
      className="max-h-44 overflow-y-auto rounded-lg border border-border bg-popover p-1"
    >
      {props.results.map((client, index) => (
        <li
          key={client.patientId}
          id={optionId(index)}
          role="option"
          aria-selected={index === props.active}
          className="cursor-pointer rounded-md px-2.5 py-1.5 text-body-lg aria-selected:bg-accent"
          onClick={() => props.onChoose(client)}
        >
          {client.displayName}
        </li>
      ))}
    </ul>
  );
}
